import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Alert, Button, Collapse, Empty, Modal, Spin, Tabs, Tag, Typography, message } from 'antd'
import { CloseOutlined, DownloadOutlined, DownOutlined, LinkOutlined, StarOutlined, UpOutlined } from '@ant-design/icons'
import { adminApi, api } from '../api'
import {
  KNOWLEDGE_SCOPE_META,
  scopeQuery,
  type KnowledgeScope,
} from '../constants/knowledgeScope'
import {
  KnowledgeFilePreviewPanel,
  downloadKnowledgeAttachment,
  useKnowledgePreviewActiveFile,
} from '../components/KnowledgeFilePreviewPanel'
import { KnowledgeItemKeyValuesPreview } from '../components/KnowledgeItemKeyValuesPreview'
import { KnowledgeItemMetaPanel, type KnowledgeItemMeta } from '../components/KnowledgeItemMetaPanel'
import { KnowledgeItemShareTab } from '../components/KnowledgeItemShareTab'
import { PageLoading } from '../components/ui'
import { validateSession } from '../lib/sessionAuth'
import { AttributeSearchDrawer } from '../components/AttributeSearchDrawer'
import {
  formatAttrKv,
  isAttributeSelected,
  parseAttrKv,
  toggleAttribute,
  type SelectedAttribute,
} from '../lib/attributeSearch'
import { knowledgeDocPreviewPath } from '../lib/knowledgeDocPreview'
import {
  canSuggestLinkRule,
  formatLinkReasonText,
  type LinkReason,
} from '../lib/linkReason'
import './SearchDocPreview.css'

const KnowledgeDocumentGraph = lazy(() =>
  import('../components/KnowledgeDocumentGraph').then((module) => ({ default: module.KnowledgeDocumentGraph })),
)

type RelatedDoc = {
  item_id: number
  title: string
  relation_hint: string
  link_reason?: LinkReason
  shared_key_values?: string[]
  shared_count?: number
  query_key_values?: string[]
  snippet?: string
  score?: number
  relevance_percent?: number
}

type RelatedGroup = {
  value: string
  keys: string[]
  items: RelatedDoc[]
  total: number
}

type RelatedRes = {
  item_id: number
  query: string
  mode?: 'key_value'
  groups: RelatedGroup[]
}

type RecommendedAttr = {
  key: string
  value: string
  label?: string
  seed_key?: string
  seed_value?: string
  seed_label?: string
  similarity?: number
}

type RecommendedDoc = {
  item_id: number
  title: string
  similarity_percent?: number
  recommend_hint?: string
  matched_attributes?: RecommendedAttr[]
}

type RecommendedRes = {
  item_id: number
  mode?: 'attr_vector'
  items: RecommendedDoc[]
}

function parseScope(raw: string | null): KnowledgeScope {
  return raw === 'personal' ? 'personal' : 'enterprise'
}

function groupPanelLabel(group: RelatedGroup): string {
  const keys = group.keys?.length ? group.keys.join(' / ') : '字段'
  return `${keys} = ${group.value}`
}

function formatRecommendKvLabel(key: string, value: string, label?: string): string {
  if (label?.trim()) return label.trim()
  const k = key.trim()
  const v = value.trim()
  return k ? `${k}=${v}` : v
}

function RecommendedDocMatches({ attrs }: { attrs: RecommendedAttr[] }) {
  if (!attrs.length) return null
  return (
    <div className="search-doc-preview-recommend-matches">
      <div className="search-doc-preview-recommend-matches-label">
        相似依据：命中字段 ↔ 相似字段
      </div>
      <ul className="search-doc-preview-recommend-matches-list">
        {attrs.slice(0, 3).map((a, i) => {
          const seed = formatRecommendKvLabel(a.seed_key ?? '', a.seed_value ?? '', a.seed_label)
          const target = formatRecommendKvLabel(a.key, a.value, a.label)
          const pct =
            typeof a.similarity === 'number' ? Math.min(100, Math.round(a.similarity * 100)) : null
          return (
            <li key={`${seed}-${target}-${i}`} className="search-doc-preview-recommend-match">
              <span className="search-doc-preview-recommend-match-side">
                <span className="search-doc-preview-recommend-match-side-label">命中</span>
                <span className="search-doc-preview-recommend-match-kv">{seed}</span>
              </span>
              <span className="search-doc-preview-recommend-match-arrow">↔</span>
              <span className="search-doc-preview-recommend-match-side">
                <span className="search-doc-preview-recommend-match-side-label">相似</span>
                <span className="search-doc-preview-recommend-match-kv">{target}</span>
              </span>
              {pct != null ? (
                <span className="search-doc-preview-recommend-match-pct">{pct}%</span>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function RelatedDocAttrs({
  doc,
  highlightKvs,
  emphasize,
  selectedAttrs,
  onToggleAttr,
}: {
  doc: RelatedDoc
  highlightKvs: string[]
  emphasize: boolean
  selectedAttrs: SelectedAttribute[]
  onToggleAttr: (attr: SelectedAttribute) => void
}) {
  const [expanded, setExpanded] = useState(emphasize)
  const attrs = doc.shared_key_values ?? []
  const count = attrs.length || doc.shared_count || 0
  const highlightSet = useMemo(() => new Set(highlightKvs), [highlightKvs])
  const queryMatched = useMemo(() => new Set(doc.query_key_values ?? []), [doc.query_key_values])

  useEffect(() => {
    if (emphasize) setExpanded(true)
  }, [emphasize])

  if (count <= 0 && !attrs.length) return null

  const tagColor = (kv: string, picked: boolean) => {
    if (picked) return 'gold'
    if (emphasize && highlightSet.has(kv)) return 'gold'
    if (!emphasize && queryMatched.has(kv)) return 'gold'
    return 'default'
  }

  return (
    <div className="search-doc-preview-related-attrs">
      <button
        type="button"
        className={`search-doc-preview-related-attrs-toggle${emphasize ? ' search-doc-preview-related-attrs-toggle--emphasized' : ''}`}
        aria-expanded={expanded}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setExpanded((v) => !v)
        }}
      >
        <span>关联字段 {count} 项</span>
        {expanded ? <UpOutlined /> : <DownOutlined />}
      </button>
      {expanded ? (
        <div className="search-doc-preview-related-attrs-tags">
          {attrs.length > 0 ? (
            attrs.map((kv) => {
              const attr = parseAttrKv(kv)
              if (!attr) {
                return (
                  <Tag key={kv} className="search-doc-preview-related-attrs-tag">
                    {kv}
                  </Tag>
                )
              }
              const picked = isAttributeSelected(selectedAttrs, attr)
              return (
                <Tag
                  key={kv}
                  className={`search-doc-preview-related-attrs-tag search-doc-preview-related-attrs-tag--pickable${picked ? ' search-doc-preview-related-attrs-tag--picked' : ''}${tagColor(kv, picked) === 'gold' ? ' search-doc-preview-related-attrs-tag--hl' : ''}`}
                  color={tagColor(kv, picked)}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onToggleAttr(attr)
                  }}
                >
                  {kv}
                </Tag>
              )
            })
          ) : (
            <span className="search-doc-preview-related-attrs-fallback">{doc.relation_hint}</span>
          )}
        </div>
      ) : null}
    </div>
  )
}

type FolderPlacement = {
  folder_path?: string
  folder_name?: string
  dimension_name?: string
}

function formatFolderPlacements(placements: FolderPlacement[] | undefined): string {
  if (!placements?.length) return ''
  return placements
    .map((p) => {
      const prefix = p.dimension_name ? `${p.dimension_name} / ` : ''
      return `${prefix}${p.folder_path || p.folder_name || ''}`.trim()
    })
    .filter(Boolean)
    .join('；')
}

export default function SearchDocPreview() {
  const navigate = useNavigate()
  const { itemId: itemIdParam } = useParams()
  const [searchParams] = useSearchParams()
  const itemId = Number(itemIdParam)
  const scope = parseScope(searchParams.get('scope'))
  const query = (searchParams.get('q') || '').trim()
  const highlightKvs = useMemo(
    () => searchParams.getAll('rel_kv').map((s) => s.trim()).filter(Boolean),
    [searchParams],
  )
  const fromItemId = Number(searchParams.get('from_item') || 0)
  const scopeParams = useMemo(() => scopeQuery(scope), [scope])

  const [authChecked, setAuthChecked] = useState(false)
  const [pageTitle, setPageTitle] = useState('文档预览')
  const [relatedLoading, setRelatedLoading] = useState(false)
  const [relatedGroups, setRelatedGroups] = useState<RelatedGroup[]>([])
  const [relatedOpenKeys, setRelatedOpenKeys] = useState<string[]>([])
  const [recommendLoading, setRecommendLoading] = useState(false)
  const [recommendedItems, setRecommendedItems] = useState<RecommendedDoc[]>([])
  const [mainTab, setMainTab] = useState<'body' | 'meta' | 'share' | 'fields' | 'graph'>('body')
  const [itemMeta, setItemMeta] = useState<KnowledgeItemMeta | null>(null)
  const [itemMetaLoading, setItemMetaLoading] = useState(false)
  const [datasetTags, setDatasetTags] = useState<{ id: number; name: string }[]>([])
  const [folderName, setFolderName] = useState('')
  const [sideTab, setSideTab] = useState<'recommend' | 'related'>('recommend')
  const [selectedAttrs, setSelectedAttrs] = useState<SelectedAttribute[]>([])
  const [attrDrawerOpen, setAttrDrawerOpen] = useState(false)
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [suggestTarget, setSuggestTarget] = useState<RelatedDoc | null>(null)
  const [suggestSubmitting, setSuggestSubmitting] = useState(false)

  const toggleSelectedAttr = useCallback((attr: SelectedAttribute) => {
    setSelectedAttrs((prev) => toggleAttribute(prev, attr))
  }, [])

  const submitLinkRuleSuggest = () => {
    const doc = suggestTarget
    const reason = doc?.link_reason
    if (!doc || !reason) return
    const srcKey = reason.anchor_keys?.[0] || ''
    const tgtKey = reason.matched_keys?.[0] || ''
    const matchValue = reason.anchor_value || ''
    if (!srcKey || !tgtKey) return
    setSuggestSubmitting(true)
    api
      .post('/public/search/link-rules/suggest', {
        scope,
        source_item_id: itemId,
        target_item_id: doc.item_id,
        rule_type: 'key_alias',
        src_key: srcKey,
        tgt_key: tgtKey,
        match_value: matchValue,
      })
      .then(() => {
        message.success('关联规则已提交，待管理员审核')
        setSuggestOpen(false)
        setSuggestTarget(null)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setSuggestSubmitting(false))
  }

  useEffect(() => {
    if (selectedAttrs.length === 0) setAttrDrawerOpen(false)
  }, [selectedAttrs.length])

  const { activeFile } = useKnowledgePreviewActiveFile(
    Number.isFinite(itemId) && itemId > 0 ? itemId : null,
    scopeParams,
    authChecked,
  )

  useEffect(() => {
    if (!Number.isFinite(fromItemId) || fromItemId <= 0 || !relatedGroups.length) return
    const keys = relatedGroups
      .filter((g) => g.items.some((d) => d.item_id === fromItemId))
      .map((g) => g.value)
    if (keys.length) setRelatedOpenKeys(keys)
  }, [relatedGroups, fromItemId])

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      const ok = await validateSession()
      if (cancelled) return
      if (!ok) {
        message.warning('预览文档需先登录')
        void navigate(
          `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`,
        )
        return
      }
      if (!cancelled) setAuthChecked(true)
    }

    void check()
    return () => {
      cancelled = true
    }
  }, [navigate])

  useEffect(() => {
    if (!authChecked || !Number.isFinite(itemId) || itemId <= 0) return
    setItemMetaLoading(true)
    adminApi
      .get<KnowledgeItemMeta & { folder_id?: number | null; folder_placements?: FolderPlacement[] }>(
        `/admin/knowledge/items/${itemId}`,
        { params: scopeParams },
      )
      .then((r) => {
        setItemMeta(r.data)
        adminApi
          .get<{ items: { id: number; name: string }[] }>(`/admin/datasets/by-item/${itemId}`)
          .then((dr) => setDatasetTags(dr.data.items || []))
          .catch(() => setDatasetTags([]))
        const fromPlacements = formatFolderPlacements(r.data.folder_placements)
        if (fromPlacements) {
          setFolderName(fromPlacements)
          return
        }
        const fid = r.data.folder_id
        if (fid != null && fid > 0) {
          void adminApi
            .get<{ items: { id: number; name: string }[] }>('/admin/knowledge/folders/labels', {
              params: scopeParams,
            })
            .then((fr) => {
              const folder = fr.data.items.find((f) => f.id === fid)
              setFolderName(folder?.name || '')
            })
            .catch(() => setFolderName(''))
        } else {
          setFolderName('')
        }
      })
      .catch(() => {
        setItemMeta(null)
        setFolderName('')
      })
      .finally(() => setItemMetaLoading(false))
  }, [authChecked, itemId, scopeParams])

  useEffect(() => {
    if (!authChecked || !Number.isFinite(itemId) || itemId <= 0) return
    setRecommendLoading(true)
    api
      .post<RecommendedRes>('/public/search/recommended-docs', {
        item_id: itemId,
        scope,
        limit: 8,
      })
      .then((r) => setRecommendedItems(r.data.items || []))
      .catch(() => setRecommendedItems([]))
      .finally(() => setRecommendLoading(false))
  }, [authChecked, itemId, scope])

  useEffect(() => {
    if (!authChecked || !Number.isFinite(itemId) || itemId <= 0) return
    setRelatedLoading(true)
    api
      .post<RelatedRes>('/public/search/related-docs', {
        item_id: itemId,
        query,
        scope,
        per_group_limit: 10,
      })
      .then((r) => setRelatedGroups(r.data.groups || []))
      .catch((e) => message.error(e.message))
      .finally(() => setRelatedLoading(false))
  }, [authChecked, itemId, query, scope])

  useEffect(() => {
    if (!authChecked || !Number.isFinite(itemId) || itemId <= 0) return
    adminApi
      .get<{ title: string }>(`/admin/knowledge/items/${itemId}`, { params: scopeParams })
      .then((r) => setPageTitle(r.data.title || `文档 #${itemId}`))
      .catch(() => setPageTitle(`文档 #${itemId}`))
  }, [authChecked, itemId, scopeParams])

  const sortedRecommendedItems = useMemo(
    () =>
      [...recommendedItems].sort(
        (a, b) => (b.similarity_percent ?? 0) - (a.similarity_percent ?? 0),
      ),
    [recommendedItems],
  )

  const collapseItems = useMemo(
    () =>
      relatedGroups.map((group) => ({
        key: group.value,
        label: (
          <div className="search-doc-preview-related-group-label">
            <span className="search-doc-preview-related-group-title">{groupPanelLabel(group)}</span>
            <span className="search-doc-preview-related-group-count">{group.total} 篇</span>
          </div>
        ),
        children: (
          <ul className="search-doc-preview-related-list">
            {group.items.map((doc) => {
              const emphasize =
                fromItemId > 0 && doc.item_id === fromItemId && highlightKvs.length > 0
              return (
              <li key={doc.item_id}>
                <div
                  className={`search-doc-preview-related-item${emphasize ? ' search-doc-preview-related-item--emphasized' : ''}`}
                >
                  <button
                    type="button"
                    className="search-doc-preview-related-item-open"
                    onClick={() => {
                      window.open(
                        knowledgeDocPreviewPath(doc.item_id, scope, query, {
                          highlightKvs: doc.shared_key_values,
                          fromItemId: itemId,
                        }),
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }}
                  >
                    <span className="search-doc-preview-related-item-head">
                      <span className="search-doc-preview-related-item-title">{doc.title}</span>
                      {typeof doc.relevance_percent === 'number' ? (
                        <span className="search-doc-preview-related-item-score">
                          相关度 {doc.relevance_percent}%
                        </span>
                      ) : null}
                    </span>
                  </button>
                  {doc.link_reason ? (
                    <Typography.Text
                      type="secondary"
                      className="search-doc-preview-link-reason"
                    >
                      {formatLinkReasonText(doc.link_reason)}
                    </Typography.Text>
                  ) : null}
                  {canSuggestLinkRule(doc.link_reason) ? (
                    <Button
                      type="link"
                      size="small"
                      className="search-doc-preview-suggest-rule"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setSuggestTarget(doc)
                        setSuggestOpen(true)
                      }}
                    >
                      提议固化为规则
                    </Button>
                  ) : null}
                  <RelatedDocAttrs
                    doc={doc}
                    highlightKvs={highlightKvs}
                    emphasize={emphasize}
                    selectedAttrs={selectedAttrs}
                    onToggleAttr={toggleSelectedAttr}
                  />
                </div>
              </li>
            )})}
            {group.total > group.items.length ? (
              <li className="search-doc-preview-related-more">
                共 {group.total} 篇，展示关联度最高的 {group.items.length} 篇
              </li>
            ) : null}
          </ul>
        ),
      })),
    [relatedGroups, scope, query, itemId, fromItemId, highlightKvs, selectedAttrs, toggleSelectedAttr],
  )

  if (!authChecked) {
    return (
      <div className="search-doc-preview-page">
        <PageLoading />
      </div>
    )
  }

  if (!Number.isFinite(itemId) || itemId <= 0) {
    return (
      <div className="search-doc-preview-page">
        <Alert type="error" message="无效的文档 ID" showIcon />
      </div>
    )
  }

  return (
    <div className="search-doc-preview-page">
      <header className="search-doc-preview-header">
        <div className="search-doc-preview-header-main">
          <Typography.Title level={5} className="search-doc-preview-title">
            {pageTitle}
          </Typography.Title>
          <Typography.Text type="secondary" className="search-doc-preview-meta">
            {KNOWLEDGE_SCOPE_META[scope].searchHint}
            {query ? ` · 检索：${query}` : null}
          </Typography.Text>
        </div>
        <div className="search-doc-preview-header-actions">
          {activeFile ? (
            <Button
              icon={<DownloadOutlined />}
              onClick={() => void downloadKnowledgeAttachment(itemId, activeFile, scopeParams)}
            >
              下载
            </Button>
          ) : null}
          <Button icon={<CloseOutlined />} onClick={() => window.close()}>
            关闭
          </Button>
        </div>
      </header>

      <div className="search-doc-preview-layout">
        <main className="search-doc-preview-main">
          <Tabs
            className="search-doc-preview-main-tabs"
            activeKey={mainTab}
            onChange={(k) => setMainTab(k as 'body' | 'meta' | 'share' | 'fields' | 'graph')}
            items={[
              {
                key: 'body',
                label: '正文',
                children: (
                  <KnowledgeFilePreviewPanel
                    itemId={itemId}
                    scopeParams={scopeParams}
                    active={mainTab === 'body'}
                    layout="page"
                    onTitleChange={setPageTitle}
                  />
                ),
              },
              {
                key: 'meta',
                label: '属性',
                children: itemMetaLoading ? (
                  <div className="search-doc-preview-attrs-loading">
                    <Spin description="加载属性…" />
                  </div>
                ) : itemMeta ? (
                  <>
                    {datasetTags.length ? (
                      <div style={{ marginBottom: 12 }}>
                        <Typography.Text type="secondary">所属数据集：</Typography.Text>
                        {datasetTags.map((d) => (
                          <Tag key={d.id} style={{ marginInlineEnd: 4 }}>
                            {d.name}
                          </Tag>
                        ))}
                      </div>
                    ) : null}
                    <KnowledgeItemMetaPanel item={itemMeta} scope={scope} folderName={folderName} />
                  </>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无法加载文档属性" />
                ),
              },
              ...(scope === 'enterprise'
                ? [
                    {
                      key: 'share' as const,
                      label: '共享',
                      children: (
                        <KnowledgeItemShareTab
                          itemId={itemId}
                          folderId={itemMeta?.folder_id ?? null}
                          scope={scope}
                          editable
                          active={mainTab === 'share'}
                        />
                      ),
                    },
                  ]
                : []),
              {
                key: 'fields',
                label: '字段抽取',
                children: (
                  <KnowledgeItemKeyValuesPreview
                    itemId={itemId}
                    scopeParams={scopeParams}
                    active={mainTab === 'fields'}
                    selectable
                    selectedAttrs={selectedAttrs}
                    onToggleAttr={toggleSelectedAttr}
                  />
                ),
              },
              {
                key: 'graph',
                label: '知识图谱',
                children: (
                  <Suspense fallback={<div className="knowledge-document-graph-centered"><Spin /></div>}>
                    <KnowledgeDocumentGraph itemId={itemId} active={mainTab === 'graph'} />
                  </Suspense>
                ),
              },
            ]}
          />
        </main>

        <aside className="search-doc-preview-related" aria-label="推荐与相关文档">
          {selectedAttrs.length > 0 ? (
            <div className="search-doc-preview-attr-search-bar">
              <Typography.Text type="secondary" className="search-doc-preview-attr-search-label">
                已选 {selectedAttrs.length} 项字段
              </Typography.Text>
              <div className="search-doc-preview-attr-search-tags">
                {selectedAttrs.map((a) => (
                  <Tag
                    key={formatAttrKv(a)}
                    closable
                    color="gold"
                    onClose={() => toggleSelectedAttr(a)}
                  >
                    {formatAttrKv(a)}
                  </Tag>
                ))}
              </div>
              <div className="search-doc-preview-attr-search-actions">
                <Button
                  type="primary"
                  size="small"
                  onClick={() => setAttrDrawerOpen(true)}
                >
                  查找同字段文档
                </Button>
                <Button type="link" size="small" onClick={() => setSelectedAttrs([])}>
                  清空
                </Button>
              </div>
            </div>
          ) : null}
          <Tabs
            className="search-doc-preview-side-tabs"
            activeKey={sideTab}
            onChange={(k) => setSideTab(k as 'recommend' | 'related')}
            items={[
              {
                key: 'recommend',
                label: (
                  <span>
                    <StarOutlined /> 推荐
                  </span>
                ),
                children: (
                  <>
                    <Typography.Text type="secondary" className="search-doc-preview-side-tab-hint">
                      按文档关键信息向量语义相似推荐；标题旁为综合相似度，下方列出具体相近字段
                    </Typography.Text>
                    <div className="search-doc-preview-side-tab-body">
                      {recommendLoading ? (
                        <div className="search-doc-preview-related-loading">
                          <Spin description="加载推荐…" />
                        </div>
                      ) : recommendedItems.length === 0 ? (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="暂无推荐（需文档关键信息向量索引；请重建联想索引并回填）"
                        />
                      ) : (
                        <ul className="search-doc-preview-related-list">
                          {sortedRecommendedItems.map((doc) => (
                            <li key={doc.item_id}>
                              <div className="search-doc-preview-related-item">
                                <button
                                  type="button"
                                  className="search-doc-preview-related-item-open"
                                  onClick={() => {
                                    window.open(
                                      knowledgeDocPreviewPath(doc.item_id, scope, query, {
                                        fromItemId: itemId,
                                      }),
                                      '_blank',
                                      'noopener,noreferrer',
                                    )
                                  }}
                                >
                                  <span className="search-doc-preview-related-item-head">
                                    <span className="search-doc-preview-related-item-title">
                                      {doc.title}
                                    </span>
                                    {typeof doc.similarity_percent === 'number' ? (
                                      <span className="search-doc-preview-related-item-score">
                                        相似 {doc.similarity_percent}%
                                      </span>
                                    ) : null}
                                  </span>
                                </button>
                                {doc.matched_attributes?.length ? (
                                  <RecommendedDocMatches attrs={doc.matched_attributes} />
                                ) : null}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </>
                ),
              },
              {
                key: 'related',
                label: (
                  <span>
                    <LinkOutlined /> 相关
                  </span>
                ),
                children: (
                  <>
                    <Typography.Text type="secondary" className="search-doc-preview-side-tab-hint">
                      {fromItemId > 0 && highlightKvs.length > 0
                        ? '已从关联跳转：展开并高亮与上一篇的关联字段'
                        : query
                          ? `「可关联=是」；按与「${query}」相关的值分组`
                          : '「可关联=是」；按字段值分组展示'}
                    </Typography.Text>
                    <div className="search-doc-preview-side-tab-body">
                      {relatedLoading ? (
                        <div className="search-doc-preview-related-loading">
                          <Spin description="加载关联文档…" />
                        </div>
                      ) : relatedGroups.length === 0 ? (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            query
                              ? '暂无与检索词相关的可关联字段'
                              : '暂无「可关联=是」的字段（请先在「字段抽取」页标记或重新抽取）'
                          }
                        />
                      ) : (
                        <Collapse
                          className="search-doc-preview-related-collapse"
                          bordered={false}
                          activeKey={relatedOpenKeys}
                          onChange={(keys) =>
                            setRelatedOpenKeys(Array.isArray(keys) ? keys : [keys])
                          }
                          items={collapseItems}
                        />
                      )}
                    </div>
                  </>
                ),
              },
            ]}
          />
        </aside>
      </div>

      <AttributeSearchDrawer
        open={attrDrawerOpen}
        onClose={() => setAttrDrawerOpen(false)}
        scope={scope}
        attributes={selectedAttrs}
        excludeItemId={itemId}
        sourceQuery={query}
      />

      <Modal
        title="提议固化为关联规则"
        open={suggestOpen}
        onOk={submitLinkRuleSuggest}
        onCancel={() => {
          setSuggestOpen(false)
          setSuggestTarget(null)
        }}
        confirmLoading={suggestSubmitting}
        okText="提交审核"
      >
        {suggestTarget?.link_reason ? (
          <>
            <Typography.Paragraph>
              将以下跨字段同值关联提交管理员审核，通过后全库生效：
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {formatLinkReasonText(suggestTarget.link_reason)}
            </Typography.Paragraph>
          </>
        ) : null}
      </Modal>
    </div>
  )
}
